<template>
  <c-loader-status :loaders="{ 'no-initial-content': [viewModel.$load] }">
    <!-- Access Code Dialog (shared for all cards in grid) -->
    <access-code-dialog
      v-model="showAccessCodeDialog"
      :loading="accessCodeLoading"
      :error-message="accessCodeError"
      @submit="onAccessCodeSubmit"
      @cancel="onAccessCodeCancel"
    />

    <v-container fluid>
      <v-row align="stretch">
        <v-col
          v-for="eventDate in props.viewModel.$items"
          :key="eventDate.$stableId"
          cols="12"
          sm="6"
          md="4"
        >
          <Transition
            name="flip"
            type="animation"
            :duration="1000"
            mode="out-in"
          >
            <v-card
              class="mx-auto d-flex flex-column h-100 border-thin elevation-1"
              max-width="none"
              :color="!eventDateIsSelected(eventDate) ? 'primary' : 'accent'"
              :key="eventDate.$stableId"
            >
              <!-- Card front -->
              <template v-if="!eventDateIsSelected(eventDate)">
                <v-img
                  class="text-white cursor-pointer"
                  height="250"
                  :src="getBackGroundImage(eventDate)"
                  :alt="`${eventDate.eventName} Event Card Image`"
                  cover
                  :onclick="
                    () =>
                      handleStartPurchaseClick(
                        eventDate.customSelectTicketsUrl,
                        eventDate.id,
                        eventDate,
                      )
                  "
                />
                <v-toolbar
                  v-if="!eventDateIsSelected(eventDate) && showAdminToolbar"
                  color="transparent"
                >
                  <slot
                    name="grid-card-admin-actions"
                    v-bind:eventDate="eventDate"
                  />
                </v-toolbar>
                <v-card-title
                  class="font-weight-black cursor-pointer"
                  :onclick="
                    () =>
                      handleStartPurchaseClick(
                        eventDate.customSelectTicketsUrl,
                        eventDate.id,
                        eventDate,
                      )
                  "
                >
                  {{ eventDate.eventName }}
                  <v-icon
                    v-if="eventDateRequiresAccessCode(eventDate)"
                    size="x-small"
                    class="ml-2"
                    title="Access code required"
                    >fal fa-lock</v-icon
                  >
                </v-card-title>
                <v-card-subtitle
                  class="pt-4 cursor-pointer"
                  :onclick="
                    () =>
                      handleStartPurchaseClick(
                        eventDate.customSelectTicketsUrl,
                        eventDate.id,
                        eventDate,
                      )
                  "
                >
                  {{ formatCardSubtitle(eventDate) }}
                </v-card-subtitle>
                <v-card-subtitle
                  v-if="
                    !eventDate.isSeries &&
                    (!staticData.isPublicUser ||
                      (staticData.isPublicUser &&
                        staticData.SHOW_EVENT_DURATION_IN_PUBLIC))
                  "
                  class="pt-1 cursor-pointer"
                  :onclick="
                    () =>
                      handleStartPurchaseClick(
                        eventDate.customSelectTicketsUrl,
                        eventDate.id,
                        eventDate,
                      )
                  "
                >
                  Ends at:
                  {{ format(eventDate.eventEndDateTimeLocal!, "h:mm bbb") }}
                </v-card-subtitle>
                <v-card-subtitle
                  class="pt-1 cursor-pointer"
                  :onclick="
                    () =>
                      handleStartPurchaseClick(
                        eventDate.customSelectTicketsUrl,
                        eventDate.id,
                        eventDate,
                      )
                  "
                >
                  <v-row>
                    <v-col>
                      <slot
                        name="grid-card-status"
                        v-bind:eventDate="eventDate"
                      />
                    </v-col>
                  </v-row>
                </v-card-subtitle>
                <v-spacer />
                <v-card-actions class="overflow-x-auto">
                  <v-btn
                    v-if="
                      eventDate.eventShortDescription ||
                      eventDate.eventLongDescription
                    "
                    variant="text"
                    :size="resolveSmallComponentSize"
                    @click="() => (selectedEventDate = eventDate)"
                  >
                    More Info
                  </v-btn>
                  <v-spacer></v-spacer>
                  <slot name="grid-card-actions" v-bind:eventDate="eventDate" />
                </v-card-actions>
              </template>
              <!-- Card Back -->
              <template v-else>
                <v-card-title>{{ eventDate.eventName }}</v-card-title>
                <v-card-text
                  v-if="eventDate.eventShortDescription"
                  class="overflow-y-auto"
                  style="max-height: 170px"
                >
                  <span
                    v-html="
                      sanitizeHtml(eventDate.eventShortDescription.trim())
                    "
                    style="white-space: pre-wrap"
                  ></span>
                  <v-btn
                    v-if="eventDate.eventLongDescription"
                    variant="text"
                    :size="resolveSmallComponentSize"
                    @click="() => (showMoreDetails = true)"
                  >
                    More Info
                  </v-btn>
                </v-card-text>
                <v-spacer></v-spacer>
                <v-card-actions class="py-sm-0 py-xs-0 mt-auto overflow-x-auto">
                  <v-btn
                    variant="text"
                    :size="resolveSmallComponentSize"
                    @click="() => (selectedEventDate = null)"
                  >
                    Back
                  </v-btn>
                  <v-spacer></v-spacer>
                  <slot
                    name="grid-card-back-actions"
                    v-bind:eventDate="eventDate"
                  />
                </v-card-actions>
              </template>
            </v-card>
          </Transition>
        </v-col>
      </v-row>
    </v-container>
    <v-bottom-sheet v-model="showMoreDetails" scrollable inset justify="center">
      <v-card class="text-center" height="70%">
        <v-btn
          variant="text"
          :size="resolveSmallComponentSize"
          @click="showMoreDetails = !showMoreDetails"
        >
          Close
        </v-btn>
        <v-card-text height="70%">
          <span
            v-html="
              sanitizeHtml(selectedEventDate!.eventLongDescription!.trim())
            "
            style="white-space: pre-wrap"
          ></span>
          <slot
            name="grid-card-back-actions"
            v-bind:eventDate="selectedEventDate!"
          />
        </v-card-text>
      </v-card>
    </v-bottom-sheet>
  </c-loader-status>
</template>

<script setup lang="ts">
import { EventDateDto } from "@/models.g";
import {
  EventDateDtoListViewModel,
  EventDateDtoViewModel,
} from "@/viewmodels.g";
import { format } from "date-fns-tz";
import { sanitizeHtml } from "@common/utils";
import { formatSeriesDateRange } from "@common/utils/series-card";
import { ref } from "vue";
import useAccessibility from "@common/composables/useAccessibility";
import usePurchase from "@common/composables/usePurchase";
import { ProvideInjectConsts } from "@common/provideInjectConsts";
import AccessCodeDialog from "@common/event/access-code-dialog.vue";

const props = withDefaults(
  defineProps<{
    viewModel: EventDateDtoListViewModel;
    showAdminToolbar: boolean;
  }>(),
  {
    showAdminToolbar: false,
  },
);

const staticData = useStaticData();

const selectedEventDate = ref<EventDateDto | null>(null);

const showMoreDetails = ref(false);

props.viewModel.$load();

const showErrorSnackBar = inject(ProvideInjectConsts.ShowErrorSnackBar) as (
  message: string,
) => void;

const purchase = usePurchase();
const startPurchase = purchase.startPurchase;
const startSeriesPurchase = purchase.startSeriesPurchase;
const initiatePurchase = purchase.initiatePurchase;
const initiateSeriesPurchase = purchase.initiateSeriesPurchase;

// Access code dialog state
const showAccessCodeDialog = ref(false);
const accessCodeLoading = ref(false);
const accessCodeError = ref<string | null>(null);
const pendingPurchaseEventDate = ref<EventDateDto | null>(null);
const pendingPurchaseUrl = ref<string | null>(null);

function getBackGroundImage(eventDate: EventDateDto) {
  if (eventDate.gridViewImage) {
    return eventDate.gridViewImage;
  } else if (eventDate.eventBannerImage) {
    return eventDate.eventBannerImage;
  }
  return "";
}

function eventDateIsSelected(eventDate: EventDateDtoViewModel) {
  return eventDate.id == selectedEventDate.value?.id;
}

/** Check if an event date requires an access code (property only exists in public app models). */
function eventDateRequiresAccessCode(eventDate: EventDateDto): boolean {
  return staticData.isPublicUser && !!eventDate.requiresAccessCode;
}

const { resolveSmallComponentSize } = useAccessibility();

const handleStartPurchaseClick = async (
  selectTicketsUrl: string | null,
  eventDateId: number | null,
  eventDate: EventDateDto,
) => {
  // Check if this event requires an access code
  if (eventDateRequiresAccessCode(eventDate)) {
    pendingPurchaseEventDate.value = eventDate;
    pendingPurchaseUrl.value = selectTicketsUrl;
    accessCodeError.value = null;
    showAccessCodeDialog.value = true;
    return;
  }

  if (eventDate.isSeries && eventDate.seriesId) {
    try {
      if (!(await startSeriesPurchase(eventDate.seriesId))) {
        showErrorSnackBar(seriesPurchaseError());
      }
    } catch {
      showErrorSnackBar(seriesPurchaseError());
    }
    return;
  }

  try {
    await startPurchase(selectTicketsUrl, eventDateId);
  } catch {
    showErrorSnackBar(
      initiatePurchase.message ||
        "An error occurred when selecting the event. Please try again.",
    );
  }
};

function seriesPurchaseError(): string {
  return (
    initiateSeriesPurchase.message ||
    "An error occurred when selecting the event. Please try again."
  );
}

function formatCardSubtitle(eventDate: EventDateDto): string {
  if (eventDate.isSeries) {
    return formatSeriesDateRange(eventDate);
  }
  return `${format(
    eventDate.eventStartDateTimeLocal!,
    "EEEE, MMMM do 'at' h:mm bbb",
  )} ${eventDate.venueTimezone ?? ""}`.trim();
}

const onAccessCodeSubmit = async (accessCode: string) => {
  if (!pendingPurchaseEventDate.value) {
    return;
  }

  accessCodeLoading.value = true;
  accessCodeError.value = null;

  const pending = pendingPurchaseEventDate.value;
  try {
    if (pending.isSeries && pending.seriesId) {
      if (!(await startSeriesPurchase(pending.seriesId, accessCode))) {
        accessCodeError.value =
          initiateSeriesPurchase.message ||
          "Invalid access code. Please try again.";
        return;
      }
    } else {
      await startPurchase(pendingPurchaseUrl.value, pending.id, accessCode);
    }
    showAccessCodeDialog.value = false;
  } catch {
    accessCodeError.value =
      initiatePurchase.message || "Invalid access code. Please try again.";
  } finally {
    accessCodeLoading.value = false;
  }
};

const onAccessCodeCancel = () => {
  accessCodeError.value = null;
  pendingPurchaseEventDate.value = null;
  pendingPurchaseUrl.value = null;
};
</script>

<!-- This isn't working, can try to fix later -->
<style lang="scss">
.flip-enter-active {
  transition: all 1s ease;
}

.flip-leave-active {
  display: none;
}

.flip-enter,
.flip-leave {
  transform: rotateY(180deg);
  opacity: 0;
}

.v-card-subtitle {
  opacity: 0.7 !important;
}
</style>
